import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api.ts'
import type { Company, CompanyDetail, Person } from './types.ts'

// One company, as its administrators or Icefield support see it. Loaded once per visit:
// for Icefield, each load is logged as a support view. Changes update this copy from ICE's
// answer rather than loading it again.

export const companyKey = (id: string) => ['company', id] as const

export function useCompany(companyId: string) {
  return useQuery({
    queryKey: companyKey(companyId),
    queryFn: () => api.get<CompanyDetail>(`/companies/${companyId}`),
    staleTime: Infinity,
  })
}

export function useCompanyCache(companyId: string) {
  const queryClient = useQueryClient()
  const update = (fn: (d: CompanyDetail) => CompanyDetail) =>
    queryClient.setQueryData<CompanyDetail>(companyKey(companyId), (d) => (d ? fn(d) : d))
  return {
    putPerson: (person: Person) =>
      update((d) => {
        const people = person.deletedAt
          ? d.people.filter((p) => p.id !== person.id)
          : d.people.some((p) => p.id === person.id)
            ? d.people.map((p) => (p.id === person.id ? person : p))
            : [...d.people, person].sort((a, b) => a.displayName.localeCompare(b.displayName))
        return { ...d, people }
      }),
    putCompany: (company: Company) => {
      update((d) => ({ ...d, company }))
      // The header shows the name and logo.
      void queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    patch: update,
    reload: () => queryClient.invalidateQueries({ queryKey: companyKey(companyId) }),
  }
}
